
import { supabase } from '@/integrations/supabase/client';
import { Project, DatabaseProject } from '@/types/project';
import { mapDatabaseProjectToProject } from '@/utils/projectMappers';

// Resolve site office UUID from localStorage (selectedSiteOfficeId may be a code like "SO-TIMUR")
const resolveSelectedSiteOfficeUuid = async (): Promise<string | null> => {
  const siteOfficeName = localStorage.getItem('selectedSiteOffice');
  if (!siteOfficeName) return null;

  // Always resolve UUID from the site_offices table by name
  // to avoid stale cached UUIDs from a previously selected site office
  const { data, error } = await supabase
    .from('site_offices')
    .select('id')
    .eq('name', siteOfficeName)
    .maybeSingle();

  if (error || !data?.id) {
    console.error('Could not resolve site office UUID by name:', siteOfficeName, error);
    // Fallback to cached UUID only if name lookup fails
    const cachedId = localStorage.getItem('selectedSiteOfficeId');
    if (cachedId && cachedId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      return cachedId;
    }
    return null;
  }

  // Update cache with correct UUID for this site office name
  localStorage.setItem('selectedSiteOfficeId', data.id);
  localStorage.setItem('selectedSiteOfficeUuid', data.id);
  return data.id;
};

// Check if current user is an administrator
const isCurrentUserAdmin = (): boolean => {
  const teamMemberStr = localStorage.getItem('currentTeamMember');
  if (!teamMemberStr) return false;
  try {
    const teamMember = JSON.parse(teamMemberStr);
    const role = (teamMember.role || '').toLowerCase();
    return role === 'administrator' || role === 'admin' || teamMember.is_admin === true;
  } catch {
    return false;
  }
};

// Fetch all projects with materials and labor filtered by site office and year
export const fetchAllProjects = async (): Promise<Project[]> => {
  try {
    const siteOfficeId = await resolveSelectedSiteOfficeUuid();
    const isAdmin = isCurrentUserAdmin();
    
    // Get selected year from localStorage
    const selectedYear = localStorage.getItem('selectedYear') || new Date().getFullYear().toString();
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${parseInt(selectedYear) + 1}-01-01`;
    
    // If no site office selected but user is admin, fetch all projects
    // If no site office selected and not admin, return empty
    if (!siteOfficeId && !isAdmin) {
      console.log('No site office selected and not admin, returning empty array');
      return [];
    }

    console.log(`=== FETCHING PROJECTS ${siteOfficeId ? `FOR SITE OFFICE: ${siteOfficeId}` : '(ALL - ADMIN MODE)'} YEAR: ${selectedYear} ===`);

    // Build query - admin sees all, regular users see filtered
    let query = supabase
      .from('projects')
      .select('*')
      .gte('created_at', yearStart)
      .lt('created_at', yearEnd)
      .order('created_at', { ascending: false });
    
    // Only filter by site office if user is not admin OR if admin has selected a specific site office
    if (siteOfficeId) {
      query = query.eq('site_office_id', siteOfficeId);
    }

    const { data: projectsData, error: projectsError } = await query;

    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
      throw projectsError;
    }

    console.log('✅ Projects fetched:', projectsData?.length || 0);

    if (!projectsData || projectsData.length === 0) {
      console.log('No projects found');
      return [];
    }

    // Fetch materials and labor for the projects
    const projectIds = projectsData.map(p => p.id);
    
    const [materialsResult, laborResult] = await Promise.all([
      supabase
        .from('materials')
        .select('*')
        .in('project_id', projectIds)
        .order('sort_order', { ascending: true }),
      supabase
        .from('labors')
        .select('*')
        .in('project_id', projectIds)
        .order('sort_order', { ascending: true })
    ]);

    if (materialsResult.error) {
      console.error('Error fetching materials:', materialsResult.error);
    }
    if (laborResult.error) {
      console.error('Error fetching labor:', laborResult.error);
    }

    const allMaterials = materialsResult.data || [];
    const allLabor = laborResult.data || [];

    console.log('✅ Materials fetched for site office:', allMaterials.length);
    console.log('✅ Labor fetched for site office:', allLabor.length);

    // Group materials and labor by project_id
    const materialsByProject = allMaterials.reduce((acc, material) => {
      if (!acc[material.project_id]) acc[material.project_id] = [];
      acc[material.project_id].push(material);
      return acc;
    }, {} as Record<string, any[]>);

    const laborByProject = allLabor.reduce((acc, labor) => {
      if (!acc[labor.project_id]) acc[labor.project_id] = [];
      acc[labor.project_id].push(labor);
      return acc;
    }, {} as Record<string, any[]>);

    // Enrich projects with their materials and labor
    const enrichedProjects = projectsData.map(project => {
      const projectMaterials = materialsByProject[project.id] || [];
      const projectLabor = laborByProject[project.id] || [];
      
      console.log(`📋 Project ${project.title}:`, {
        id: project.id,
        siteOfficeId: project.site_office_id,
        materialsCount: projectMaterials.length,
        laborCount: projectLabor.length
      });

      return {
        ...project,
        materials: projectMaterials,
        labors: projectLabor
      };
    });

    console.log('\n=== MAPPING PROJECTS TO LOCAL FORMAT ===');
    
    // Map to local format
    const mappedProjects = enrichedProjects
      .filter(project => {
        if (!project || !project.id || !project.title) {
          console.warn('⚠️ Skipping invalid project:', project);
          return false;
        }
        return true;
      })
      .map(project => {
        try {
          console.log(`🔄 Mapping project: ${project.title}`);
          
          const formattedDbProject: DatabaseProject = {
            id: project.id,
            title: project.title,
            project_number: project.project_number,
            pic: project.pic,
            supervisor: (project as any).supervisor || '',
            status: project.status,
            progress: project.progress,
            actual_progress: (project as any).actual_progress ?? null,
            start_date: project.start_date,
            end_date: project.end_date,
            created_at: project.created_at,
            updated_at: project.updated_at,
            description: project.description,
            project_type: project.project_type,
            location: project.location,
            budget_rab: (project as any).budget_rab ?? 0,
             global_budget: (project as any).global_budget ?? 0,
             global_actual: (project as any).global_actual ?? 0,
            schedule_pdf_url: (project as any).schedule_pdf_url || '',
            materials: project.materials || [],
            labors: project.labors || [],
            metadata: {}
          };
          
          const mappedProject = mapDatabaseProjectToProject(formattedDbProject);
          console.log(`✅ Final mapped project ${project.title}:`, {
            materialsCount: mappedProject.materials?.length || 0,
            laborCount: mappedProject.labor?.length || 0
          });
          
          return mappedProject;
        } catch (err) {
          console.error('❌ Error mapping project:', project.title, err);
          return null;
        }
      })
      .filter((p): p is Project => p !== null);

    console.log('\n=== FINAL RESULTS ===');
    console.log(`Total projects mapped:`, mappedProjects.length);

    return mappedProjects;
  } catch (error) {
    console.error('❌ Error in fetchAllProjects:', error);
    throw error;
  }
};

// Fetch a single project by ID (admin can see all, regular users filtered by site office)
export const fetchProjectById = async (projectId: string): Promise<Project | null> => {
  try {
    const siteOfficeId = await resolveSelectedSiteOfficeUuid();
    const isAdmin = isCurrentUserAdmin();
    
    if (!siteOfficeId && !isAdmin) {
      console.log('No site office selected and not admin');
      return null;
    }

    console.log(`=== FETCHING PROJECT: ${projectId} ${siteOfficeId ? `FOR SITE OFFICE: ${siteOfficeId}` : '(ADMIN MODE)'} ===`);

    // Build query - admin can see any project, regular users filtered by site office
    let projectQuery = supabase
      .from('projects')
      .select('*')
      .eq('id', projectId);
    
    // Only filter by site office if not admin
    if (siteOfficeId && !isAdmin) {
      projectQuery = projectQuery.eq('site_office_id', siteOfficeId);
    }

    // Fetch project, materials, and labor in parallel
    const [projectResult, materialsResult, laborResult] = await Promise.all([
      projectQuery.maybeSingle(),
      supabase
        .from('materials')
        .select('*')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true }),
      supabase
        .from('labors')
        .select('*')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true })
    ]);
    
    if (projectResult.error) {
      console.error(`Error fetching project ${projectId}:`, projectResult.error);
      return null;
    }
    
    if (!projectResult.data) {
      console.log(`No project found with ID ${projectId}`);
      return null;
    }

    const projectData = projectResult.data;
    const materialsData = materialsResult.data || [];
    const laborData = laborResult.data || [];

    console.log('✅ Project fetched:', projectData.title);
    console.log(`✅ Materials:`, materialsData.length);
    console.log(`✅ Labor:`, laborData.length);

    // Create enriched project
    const enrichedProject = {
      ...projectData,
      materials: materialsData,
      labors: laborData
    };

    const formattedData: DatabaseProject = {
      id: enrichedProject.id,
      title: enrichedProject.title,
      project_number: enrichedProject.project_number,
      pic: enrichedProject.pic,
      supervisor: (enrichedProject as any).supervisor || '',
      status: enrichedProject.status,
      progress: enrichedProject.progress,
      actual_progress: (enrichedProject as any).actual_progress ?? null,
      start_date: enrichedProject.start_date,
      end_date: enrichedProject.end_date,
      created_at: enrichedProject.created_at,
      updated_at: enrichedProject.updated_at,
      description: enrichedProject.description,
      project_type: enrichedProject.project_type,
      location: enrichedProject.location,
      budget_rab: (enrichedProject as any).budget_rab ?? 0,
       global_budget: (enrichedProject as any).global_budget ?? 0,
       global_actual: (enrichedProject as any).global_actual ?? 0,
      schedule_pdf_url: (enrichedProject as any).schedule_pdf_url || '',
      materials: enrichedProject.materials,
      labors: enrichedProject.labors,
      metadata: {}
    };
    
    const mappedProject = mapDatabaseProjectToProject(formattedData);
    console.log(`✅ Final mapped single project:`, {
      name: mappedProject.name,
      materialsCount: mappedProject.materials?.length || 0,
      laborCount: mappedProject.labor?.length || 0
    });
    
    return mappedProject;
  } catch (error) {
    console.error(`❌ Error loading project ${projectId}:`, error);
    return null;
  }
};
